/**
 * "Managing associated persons" tour.
 *
 * Walks the user through finding the Associated Persons section on a client
 * detail page, opening the Add Person form, and explaining what the form
 * captures (which varies by client type).
 *
 * Targets:
 *   tr.clickable                          — any client row in the list
 *   data-tour="section-tab-associated"    — Associated Persons section nav button
 *   data-tour="add-person-btn"            — "Add Person" button
 *   data-tour="save-person"               — "Add Person" save button in modal
 *
 * Person types per client type and form field labels mirror PersonForm.vue
 * (around lines 186-200) and ClientAssociatedPersons.vue. Keep in sync.
 */

import { NEXT, BACK, START, SKIP, DONE, advanceWhenAppears } from './_helpers.js';

function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve) => {
        if (document.querySelector(selector)) return resolve();
        const start = Date.now();
        const check = () => {
            if (document.querySelector(selector)) return resolve();
            if (Date.now() - start > timeout) return resolve();
            setTimeout(check, 50);
        };
        check();
    });
}

export const associatedPersonsTour = {
    id: 'associated-persons',
    title: 'Managing associated persons',
    description: 'How to add officers, beneficial owners, trustees, and other associated persons to a client record — including who needs to be on file for each client type.',
    autoLaunchOn: { page: 'clients' },

    steps: [
        {
            id: 'intro',
            title: 'Associated persons',
            text: `An "associated person" is anyone connected to a client who needs to be
                on the file for AML purposes — directors, beneficial owners, trustees,
                authorised representatives, and so on. The required list depends on the
                client type.`,
            buttons: [START],
        },
        {
            id: 'pick-row',
            attachTo: { element: 'table.aml_table tbody tr.clickable', on: 'top' },
            beforeShowPromise: () => waitForElement('table.aml_table tbody tr.clickable'),
            title: 'Open a client',
            text: `Click any row in the client list to open that client's detail page.
                Associated persons are managed per-client, so you need to be on a
                specific client's page first.`,
            // See editClient.js for why we watch for the destination
            // marker instead of relying on advanceOn-on-row-click.
            when: advanceWhenAppears('[data-tour="section-tab-associated"]'),
        },
        {
            id: 'section-tab',
            attachTo: { element: '[data-tour="section-tab-associated"]', on: 'bottom' },
            beforeShowPromise: () => waitForElement('[data-tour="section-tab-associated"]'),
            title: 'Open the Associated Persons section',
            text: `The detail page is one long scroll, but the section nav at the top
                jumps you to specific blocks. Click <strong>Associated Persons</strong>
                to scroll to that section.`,
            advanceOn: { selector: '[data-tour="section-tab-associated"]', event: 'click' },
        },
        {
            id: 'add-btn',
            attachTo: { element: '[data-tour="add-person-btn"]', on: 'left' },
            beforeShowPromise: () => waitForElement('[data-tour="add-person-btn"]'),
            title: 'Click "Add Person"',
            text: `Click <strong>Add Person</strong> to open the add form. Existing
                people are listed below — you can edit or remove them via the icons on
                each row.`,
            advanceOn: { selector: '[data-tour="add-person-btn"]', event: 'click' },
        },
        {
            id: 'role-options',
            attachTo: { element: '.aml_modal_overlay .aml_modal', on: 'right' },
            beforeShowPromise: () => waitForElement('[data-tour="save-person"]'),
            // Cancel if the modal closes (X or Cancel button) — the
            // save-person button lives inside the modal and goes away
            // with it.
            cancelIfMissing: '[data-tour="save-person"]',
            title: 'Roles available depend on client type',
            text: `The <strong>Role(s)</strong> dropdown shows different options based on
                the client's type:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Body Corporate:</strong> Director, Company Secretary,
                        Shareholder (&gt;25% / &lt;25%), Ultimate Holding Company.</li>
                    <li><strong>Trust:</strong> Trustee, Settlor, Appointor, Guardian /
                        Protector, Named Beneficiary, Class of Beneficiary, Unitholder.</li>
                    <li><strong>Partnership:</strong> General / Limited / Managing Partner.</li>
                    <li><strong>Unincorporated:</strong> Office Bearer, Committee Member,
                        Key Person, Member.</li>
                </ul>
                Beneficial-owner roles are split into two for non-Individual client types:
                <em>Beneficial Owner (&gt;25% ownership)</em> and
                <em>Beneficial Owner (effective control)</em>.
                <br><br>
                Universal across all types: <em>Authorised Representative</em>,
                <em>Agent</em>, <em>Power of Attorney</em>,
                <em>Third Party (giving instructions)</em>, <em>Other</em>.`,
            // No Previous button on this step — the prior tour step lives
            // outside the modal that the user has just opened. Going back
            // to it while the modal is still on screen reads as broken.
            buttons: [NEXT],
        },
        {
            id: 'core-fields',
            attachTo: { element: '.aml_modal_overlay .aml_modal', on: 'right' },
            cancelIfMissing: '[data-tour="save-person"]',
            title: 'The core fields',
            text: `Required and conditional fields:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Name</strong> (or <em>Class Description</em> for class
                        beneficiaries).</li>
                    <li><strong>Role(s)</strong> — multi-select.</li>
                    <li><strong>Director ID</strong> — required if Director role
                        selected, 15-digit format.</li>
                    <li><strong>Ownership %</strong> — appears for shareholder /
                        beneficial-owner roles.</li>
                    <li><strong>DOB, Address, Authority Date, Authority Scope</strong>
                        (Full / Limited / Specific transaction).</li>
                </ul>`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'verification',
            attachTo: { element: '.aml_modal_overlay .aml_modal', on: 'right' },
            cancelIfMissing: '[data-tour="save-person"]',
            title: 'Identity verification',
            text: `For each person you record their identity details:
                <ul style="margin:6px 0 0 18px;padding:0;">
                    <li><strong>Person Type</strong> — Individual or Entity. Entity
                        switches the form to capture an organisation rather than a
                        natural person.</li>
                    <li><strong>Primary identification</strong> — pick a document type
                        from the category groups (primary photographic, primary
                        non-photographic, etc.).</li>
                    <li><strong>Secondary identification</strong> — optional, for
                        cases where a single primary doc isn\'t enough.</li>
                    <li>For non-company entities, an <strong>Entity Verification
                        Confirmation</strong> checkbox lets you record that the
                        entity has been independently verified.</li>
                </ul>
                You don\'t set an "identity verified" flag manually — that\'s
                determined by the verification result.
                <br><br>
                After saving, the system automatically runs
                <strong>PEP, sanctions and regulatory-list screening</strong>. Any
                matches surface in the expandable row for assessment
                (Confirmed / False Positive).`,
            buttons: [BACK, NEXT],
        },
        {
            id: 'finish',
            attachTo: { element: '.aml_modal_overlay .aml_modal', on: 'right' },
            cancelIfMissing: '[data-tour="save-person"]',
            title: 'Save when ready',
            text: `Click <strong>Add Person</strong> to save this person to the client's
                file, or <strong>Cancel</strong> to discard. Once saved, the person
                appears in the list — you can edit them via the pencil icon or remove
                them with the × icon.`,
            buttons: [BACK, DONE],
        },
    ],
};
